import { useMemo, useState } from "react";
import inventoryAPI from "../../../../api/inventory/inventoryApi";
import {
  ADJUSTMENT_TYPES,
  getReasonsForType,
} from "../../../../constants/inventoryConstants";
import { showError, showSuccess } from "../../../../utils/toastUtils";
import LocationSelect from "../common/LocationSelect";
import ProductSKUSearch from "../common/ProductSKUSearch";

export default function BulkUpdateTab({ defaultLocationId, onSuccess }) {
  const INITIAL_FORM_DATA = {
    locationId: defaultLocationId || "",
    location: "",
    adjustmentType: "add",
    reason: "stock_take_increase",
    notes: "",
    items: [],
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  /***** state change  *****/
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const reasons = useMemo(
    () => getReasonsForType(formData.adjustmentType),
    [formData.adjustmentType],
  );
  const excludeIds = useMemo(
    () => formData.items.map((i) => i.inventoryItemId),
    [formData.items],
  );

  const addItem = (product) => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          inventoryItemId: product.inventoryItemId,
          sku: product.sku,
          productName: product.productName,
          variantName: product.variantName,
          available: product.stock || 0,
          delta: 1,
          unitCost: product.price ? Number(product.price) : 0,
        },
      ],
    }));
  };

  const updateLine = (idx, field, value) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === idx ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const removeLine = (idx) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== idx),
    }));
  };

  const clearAll = () => setFormData((prev) => ({ ...prev, items: [] }));

  const totals = useMemo(() => {
    let totalQty = 0;
    let totalValue = 0;
    formData.items.forEach((i) => {
      const qty = parseInt(i.delta, 10) || 0;
      totalQty += qty;
      totalValue += qty * (parseFloat(i.unitCost) || 0);
    });
    return { totalQty, totalValue };
  }, [formData.items]);

  const hasInvalidLine = formData.items.some((i) => {
    const qty = parseInt(i.delta, 10);
    if (isNaN(qty) || qty <= 0) return true;
    if (formData.adjustmentType === "subtract" && qty > i.available)
      return true;
    return false;
  });

  const handleSave = async () => {
    if (formData.items.length === 0 || !formData.locationId || !formData.reason)
      return;
    setLoading(true);
    try {
      const result = await inventoryAPI.adjust({
        locationId: formData.locationId,
        adjustmentType: formData.adjustmentType,
        reason: formData.reason,
        notes: formData.notes,
        items: formData.items.map((i) => ({
          inventoryItemId: i.inventoryItemId,
          delta: parseInt(i.delta, 10),
          unitCost: i.unitCost ? parseFloat(i.unitCost) : null,
        })),
      });
      showSuccess(`${result.itemsUpdated} items updated successfully`);
      setFormData(INITIAL_FORM_DATA);
      onSuccess?.(result);
    } catch (err) {
      const msg = err.response?.data?.error || "Bulk update failed";
      showError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Top controls */}
      <div className="row mb-3">
        <div className="col-md-8">
          <label className="form-label small">Location *</label>
          <LocationSelect
            name="locationId"
            value={formData.locationId}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col-md-4">
          <label className="form-label small">Location *</label>
          <input
            type="text"
            className="form-control"
            value={formData.location}
            onChange={handleChange}
            name="location"
            placeholder="A1, C3"
          />
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-md-6">
          <label className="form-label small">Adjustment Type *</label>
          <select
            className="form-select"
            name="adjustmentType"
            value={formData.adjustmentType}
            onChange={handleChange}
          >
            {ADJUSTMENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label small">Default Reason *</label>
          <select
            className="form-select"
            name="reason"
            value={formData.reason}
            onChange={handleChange}
          >
            <option value="">— Select —</option>
            {reasons.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Search to add */}
      <div className="mb-3">
        <label className="form-label small">Add Products</label>
        <ProductSKUSearch
          onSelect={addItem}
          excludeIds={excludeIds}
          locationId={formData.locationId}
          autoFocus
        />
        <div className="form-text small">
          <i className="ti ti-keyboard me-1" />
          Tip: USB barcode scanner works — just focus the field and scan.
        </div>
      </div>

      {/* Cart */}
      {formData.items.length > 0 ? (
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong className="small">
              Selected Items ({formData.items.length})
            </strong>
            <button
              type="button"
              className="btn btn-sm btn-link text-danger"
              onClick={clearAll}
            >
              <i className="ti ti-trash me-1" /> Clear all
            </button>
          </div>
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead className="table-light">
                <tr>
                  <th>Product</th>
                  <th style={{ width: 130 }}>SKU</th>
                  <th style={{ width: 80 }}>Current</th>
                  <th style={{ width: 110 }}>
                    Qty{" "}
                    {formData.adjustmentType === "add"
                      ? "(+)"
                      : formData.adjustmentType === "subtract"
                        ? "(−)"
                        : "(set)"}
                  </th>
                  <th style={{ width: 100 }}>Unit Cost</th>
                  <th style={{ width: 90 }}>New Total</th>
                  <th style={{ width: 40 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {formData.items.map((line, idx) => {

                  const qty = parseInt(line.delta, 10) || 0;

                  let newTotal = line.available;
                  if (formData.adjustmentType === "add")
                    newTotal = line.available + qty;
                  else if (formData.adjustmentType === "subtract")
                    newTotal = line.available - qty;
                  else newTotal = qty;
                  const lineInvalid =
                    qty <= 0 ||
                    (formData.adjustmentType === "subtract" &&
                      qty > line.available);

                  return (
                    <tr
                      key={line.inventoryItemId}
                      className={lineInvalid ? "table-danger" : ""}
                    >
                      <td>
                        <div className="fw-semibold small">
                          {line.productName}
                        </div>
                        {line.variantName && (
                          <div className="text-muted small">
                            {line.variantName}
                          </div>
                        )}
                      </td>
                      <td>
                        <code className="small">{line.sku}</code>
                      </td>
                      <td className="small">{line.available}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          className="form-control form-control-sm"
                          value={line.delta}
                          onChange={(e) =>
                            updateLine(idx, "delta", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="form-control form-control-sm"
                          value={line.unitCost}
                          onChange={(e) =>
                            updateLine(idx, "unitCost", e.target.value)
                          }
                        />
                      </td>
                      <td
                        className={
                          newTotal < 0
                            ? "text-danger fw-semibold"
                            : "fw-semibold"
                        }
                      >
                        {newTotal}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-link text-danger p-0"
                          onClick={() => removeLine(idx)}
                        >
                          <i className="ti ti-x" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div className="d-flex justify-content-between bg-light p-2 rounded small">
            <span>
              <strong>{formData.items.length}</strong> items · Total qty change:{" "}
              <strong>
                {formData.adjustmentType === "subtract" ? "−" : "+"}
                {totals.totalQty}
              </strong>
            </span>
            <span>
              Value impact: <strong>₹{totals.totalValue.toFixed(2)}</strong>
            </span>
          </div>
        </div>
      ) : (
        <div className="text-center text-muted py-4 border rounded mb-3">
          <i className="ti ti-package fs-1 d-block mb-2 opacity-50" />
          No items added yet. Search above to add products.
        </div>
      )}

      {/* Notes */}
      <div className="mb-3">
        <label className="form-label small">Notes (optional)</label>
        <textarea
          className="form-control"
          rows="2"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          placeholder="e.g. Monthly cycle count — Oct 2026"
        />
      </div>

      <div className="d-flex justify-content-end gap-2 pt-2 border-top">
        <button
          type="button"
          className="btn btn-primary"
          disabled={
            loading ||
            formData.items.length === 0 ||
            !formData.locationId ||
            !formData.reason ||
            hasInvalidLine
          }
          onClick={handleSave}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              Saving...
            </>
          ) : (
            <>
              <i className="ti ti-check me-1" /> Update Stock (
              {formData.items.length})
            </>
          )}
        </button>
      </div>
    </div>
  );
}
